/**
 * What a sitting has been worth, skill by skill.
 *
 * Every skill that earns anything is put on the list, with what it has earned
 * since, how fast, and how long the next level is at that pace. Nothing here
 * asks the world anything: it is the same experience the drops are made of,
 * added up.
 */

import { useEffect, useState } from 'react';
import {
  experienceForLevel,
  experienceToNextLevel,
  levelForExperience,
  levelProgress,
  SKILL_NAMES,
} from '@inverse/common';
import { SkillIcon } from './SkillIcon';

/** One skill being followed, from when it was first seen to earn. */
export interface Tracked {
  skill: number;
  /** When the first of it was earned, and how much since. */
  since: number;
  gained: number;
  /** When the last of it was earned, so one left alone can say so. */
  lastAt: number;
}

/** A skill with nothing earned for this long is resting rather than being trained. */
const RESTING_MS = 5 * 60 * 1000;

function per(hours: number, amount: number): number {
  return hours > 0 ? amount / hours : 0;
}

function shortTime(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return 'under a minute';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function count(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 10_000) return `${Math.round(value / 1000)}K`;
  return Math.round(value).toLocaleString();
}

export function XpTracker({
  tracked,
  experience,
  onForget,
  onReset,
}: {
  tracked: readonly Tracked[];
  experience: readonly number[];
  onForget: (skill: number) => void;
  onReset: () => void;
}): JSX.Element {
  // Ticks over once a second so the rates and the times move while nothing arrives.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="xp-tracker">
      <div className="xp-tracker-head">
        <span>This sitting</span>
        <button type="button" title="Start counting again from now" onClick={onReset}>
          Reset
        </button>
      </div>
      {tracked.map((one) => {
        const held = experience[one.skill] ?? 0;
        const level = levelForExperience(held);
        const hours = Math.max(1 / 60, (now - one.since) / 3_600_000);
        const rate = per(hours, one.gained);
        const toGo = experienceToNextLevel(held);
        const resting = now - one.lastAt > RESTING_MS;
        const name = SKILL_NAMES[one.skill] ?? 'Skill';
        return (
          <div key={one.skill} className={resting ? 'xp-row resting' : 'xp-row'}>
            <div className="xp-row-top">
              <SkillIcon skill={name} />
              <strong>{name}</strong>
              <span className="xp-row-gained">+{count(one.gained)}</span>
              <button
                type="button"
                className="xp-row-forget"
                title="Stop following it"
                onClick={() => onForget(one.skill)}
              >
                ✕
              </button>
            </div>
            <div
              className="xp-bar"
              title={
                level < 99
                  ? `${Math.round(levelProgress(held) * 100)}% of the way to ${level + 1}`
                  : 'Level 99'
              }
            >
              <span style={{ width: `${Math.round(levelProgress(held) * 100)}%` }} />
            </div>
            <div className="xp-row-facts">
              {resting ? <span>Resting</span> : <span>{count(rate)} an hour</span>}
              {level < 99 ? (
                <span>
                  {count(toGo)} to {level + 1}
                  {!resting && rate > 0 ? `, ${shortTime((toGo / rate) * 3_600_000)}` : ''}
                </span>
              ) : (
                <span>{count(held - experienceForLevel(99))} past 99</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
